import {
  alignForecastToShifts,
  resolveTimelineShifts,
} from '../../../lib/shiftOverlay';
import {
  capacityOutlet,
  dayTotalsFromReservations,
  filterTimelineForOutlet,
  liveFloorFromReservations,
  outletGuestForecast,
  reservationsForOutlet,
  ribbonKpisForOutlet,
  sortLiveOutletOptions,
} from '../../../lib/timelineOutletFilter';
import type { OpsCommandLivePayload } from '../../../types';
import { pointGuests } from '../../ribbon/ribbonPieces';

export function liveWidgetScope(live: OpsCommandLivePayload) {
  const outlets = sortLiveOutletOptions(live.outlets ?? []);
  const selectedOutletId =
    outlets.length === 1 ? outlets[0]?.id ?? null : null;
  const selectedOutlet =
    outlets.find(o => o.id === selectedOutletId) ?? null;
  const cap = capacityOutlet(outlets, selectedOutletId) ?? selectedOutlet;
  const kpiOutlet =
    selectedOutlet && cap
      ? {
          ...selectedOutlet,
          seats: cap.seats,
          tables: cap.tables,
          coversToday: selectedOutlet.coversToday ?? cap.coversToday,
          bookedAhead: selectedOutlet.bookedAhead ?? cap.bookedAhead,
          upcoming: selectedOutlet.upcoming ?? cap.upcoming,
          seated: selectedOutlet.seated || cap.seated,
          occTables: selectedOutlet.occTables || cap.occTables,
          spendCover: selectedOutlet.spendCover ?? cap.spendCover,
          avgSeatMin: selectedOutlet.avgSeatMin ?? cap.avgSeatMin,
          lostGapCovers: selectedOutlet.lostGapCovers ?? cap.lostGapCovers,
        }
      : selectedOutlet;
  const scopedTimeline = filterTimelineForOutlet(
    live.serviceTimeline,
    selectedOutletId,
  );
  const timelineShifts = resolveTimelineShifts(scopedTimeline);
  const forecast = live.forecast ?? null;
  const fc = forecast
    ? alignForecastToShifts(
        outletGuestForecast(
          forecast,
          live.serviceTimeline ?? null,
          selectedOutletId,
          kpiOutlet,
        ),
        timelineShifts,
      )
    : null;
  const outletReservations = reservationsForOutlet(
    live.serviceTimeline,
    selectedOutletId,
  );
  const dayTotals = fc
    ? dayTotalsFromReservations(
        outletReservations,
        fc.openH,
        fc.closeH,
        fc.nowH,
      )
    : { started: 0, ahead: 0, next2h: 0, total: 0 };
  const liveFloor = fc
    ? liveFloorFromReservations(
        outletReservations,
        fc.openH,
        fc.closeH,
        fc.nowH,
      )
    : null;
  const scopedKpis = ribbonKpisForOutlet(
    live.kpis,
    kpiOutlet,
    dayTotals,
    liveFloor,
  );
  const bookedByClose = fc
    ? Math.max(
        fc.projectedCovers,
        dayTotals.total,
        scopedKpis.covers + scopedKpis.incoming,
      )
    : scopedKpis.covers + scopedKpis.incoming;
  const stillBooked = Math.max(
    0,
    bookedByClose - scopedKpis.covers - scopedKpis.incoming,
  );
  const bench = selectedOutletId ? null : live.timelineBenchmarks ?? null;
  return {
    selectedOutletId,
    selectedOutlet,
    scopedTimeline,
    timelineShifts,
    fc,
    scopedKpis,
    bookedByClose,
    stillBooked,
    bench,
    peakGuests: fc ? pointGuests(fc.peak) : 0,
    lostGapCovers: selectedOutlet?.lostGapCovers ?? 0,
    connected: live.connected ?? [],
    outlets: live.outlets ?? [],
    recovery: live.recovery ?? null,
    kitchenBriefing: live.kitchenBriefing ?? null,
    hasPos: (live.connected ?? []).includes('pos'),
  };
}
